import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { FIELD_MAX_LENGTHS } from '@/lib/validation-constants';
import { getDevices } from '@/features/devices/api/devices-api';
import type { Unit, CreateUnitRequest, Device } from '@/types/api';

export function UnitModal({
  unit,
  buildingId,
  onClose,
  onSave,
}: {
  unit: Unit | null;
  buildingId: string;
  onClose: () => void;
  onSave: (data: CreateUnitRequest) => Promise<void>;
}) {
  // May 14 batch: Floor field removed from the Unit form. floorNumber stays
  // as a nullable column on the entity for legacy data; the UI just stops
  // surfacing it on create or edit.
  const [formData, setFormData] = useState<CreateUnitRequest>({
    buildingId: unit?.buildingId || buildingId,
    unitNumber: unit?.unitNumber || '',
    area: unit?.area,
    tenantName: unit?.tenantName,
    tenantContact: unit?.tenantContact,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreateUnitRequest, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unitDevices, setUnitDevices] = useState<Device[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  useEffect(() => {
    if (!unit?.id) return;
    let cancelled = false;
    setDevicesLoading(true);
    getDevices({ unitId: unit.id, perPage: 10 })
      .then((r) => { if (!cancelled) setUnitDevices(r.items); })
      .catch((err) => console.error('Failed to load unit devices', err))
      .finally(() => { if (!cancelled) setDevicesLoading(false); });
    return () => { cancelled = true; };
  }, [unit?.id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const newErrors: Partial<Record<keyof CreateUnitRequest, string>> = {};
    if (!formData.unitNumber.trim()) newErrors.unitNumber = 'Unit number is required.';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={unit ? 'Edit Unit' : 'Add New Unit'}
      description="Enter the unit details below"
    >
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Unit Number"
          required
          maxLength={FIELD_MAX_LENGTHS.UNIT_NUMBER}
          value={formData.unitNumber}
          onChange={(e) => { setFormData({ ...formData, unitNumber: e.target.value }); setErrors(prev => ({ ...prev, unitNumber: undefined })); setSubmitError(null); }}
          placeholder="e.g., 101"
          error={errors.unitNumber}
        />

        {unit && (
          <div className="space-y-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 dark:border-gray-700 dark:bg-gray-800/60">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-gray-400">
                Associated Devices
              </div>
              <Link
                to={`/units/${unit.id}`}
                onClick={onClose}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Manage devices &rarr;
              </Link>
            </div>
            {devicesLoading ? (
              <div className="text-sm text-neutral-500 dark:text-gray-400">Loading&hellip;</div>
            ) : unitDevices.length === 0 ? (
              <div className="text-sm text-neutral-600 dark:text-gray-300">No devices assigned to this unit.</div>
            ) : (
              <ul className="space-y-1">
                {unitDevices.map((d) => (
                  <li key={d.id} className="flex flex-col text-sm">
                    <span className="font-mono text-gray-900 dark:text-gray-100">{d.serialNumber || '—'}</span>
                    <span className="font-mono text-xs text-neutral-500 dark:text-gray-400">{d.devEui}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {submitError && (
          <p className="text-sm text-error-600">{submitError}</p>
        )}

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} className="flex-1">
            {unit ? 'Update' : 'Create'} Unit
          </Button>
        </div>
      </form>
    </Modal>
  );
}
